import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Block } from '@sorare/core/src/atoms/layout/Block';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { PlayerAvatar } from 'components/player/PlayerAvatar';

import type { PlayerListItem_player } from './__generated__/index.graphql';

interface Props {
  player: PlayerListItem_player;
  subtitle?: string;
  children?: ReactNode;
}

const Root = styled(Block)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Avatar = styled(PlayerAvatar)`
  height: 60px;
  width: 60px;
`;
const Text = styled.div`
  margin-left: 20px;
`;

export const PlayerListItem = ({ player, subtitle, children }: Props) => {
  const { sport } = useSportContext();
  const { slug, displayName } = player;

  return (
    <Root
      to={generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
        sport,
        params: { slug },
      })}
    >
      <Horizontal gap={0}>
        <Avatar player={player} />
        <Text>
          <BodyM bold>{displayName}</BodyM>
          <BodyM color="var(--c-nd-600)">{subtitle}</BodyM>
        </Text>
      </Horizontal>
      <div>{children}</div>
    </Root>
  );
};

PlayerListItem.fragments = {
  player: gql`
    fragment PlayerListItem_player on AnyPlayerInterface {
      slug
      displayName
      ...GameplayPlayerAvatar_anyPlayer
    }
    ${PlayerAvatar.fragments.anyPlayer}
  ` as TypedDocumentNode<PlayerListItem_player>,
};
